// Externe Bibliotheken
import { FormEvent, KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { NavLink, useNavigate } from 'react-router';
import axios from 'axios';

// Interne Module
import * as storage from '@/_helper/storage';
import tracking from '@/_helper/tracking';
import { checkIsAllowToBookService } from '@/_helper/checkAccess';
import { validateEmail } from '@/_helper/validation';
import { CONTACT } from '@/_config/constants';
import BookingData, { EmergencyContactData } from '@/model/common/BookingData';
import { PageState } from '@/model/common/PageState';
import { PhoneInputFieldData } from '@/model/form/PhoneInput';

// Komponenten
import Header from '@/comp/header/Header';
import Footer from '@/comp/footer/Footer';
import PhoneInput from '@/comp/form/phoneInput/PhoneInput';
import StatusPage from '@/comp/statusPage/statusPage';
import Button from '@/comp/button/button';
import BackButton from '@/comp/backButton/backButton';

// Styles
import './emergency-contact.scss';

const EmergencyContact = () => {
    const navigate = useNavigate();
    const bookingData: BookingData = storage.get('bookingData') ?? {};

    const [pageState, setPageState] = useState<PageState>(PageState.Idle);
    const [alreadyTested, setAlreadyTested] = useState<boolean>(false);
    const [agbChecked, setAgbChecked] = useState<boolean>(false);

    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [mail, setMail] = useState('');
    const [phoneData, setPhoneData] = useState<PhoneInputFieldData>();
    const phoneNumber = useMemo(
        () => phoneData && phoneData?.phoneCountryPrefix + phoneData?.phoneAreaCode + phoneData?.phoneNumber,
        [phoneData]
    );

    const firstNameValid = useMemo(() => firstName.length >= 2, [firstName]);
    const lastNameValid = useMemo(() => lastName.length >= 2, [lastName]);
    const phoneValid = useMemo(() => phoneData !== undefined && phoneData?.lengthValid === true, [phoneData]);
    const mailValid = useMemo(() => validateEmail(mail), [mail]);

    const textInputsValid = useMemo(
        () => firstNameValid && lastNameValid && phoneValid && mailValid,
        [firstName, lastName, phoneData, mail]
    );
    const formValid = useMemo(() => textInputsValid && agbChecked, [textInputsValid, agbChecked]);

    useEffect(() => {
        document.title = 'Ihre Notfall-Kontaktdaten' + window.$documentTitle;
        if (
            window.$allowEmergencyContact === 'false' ||
            (window.deactivateServices as Array<string>).includes('EmergencyContact')
        ) {
            navigate('/');
            return;
        }
        if (!checkIsAllowToBookService('EmergencyContact')) {
            return;
        }
        tracking('loaded', 'emergencyContact');

        const emergencyContactData = bookingData.data.emergencyContactData;
        emergencyContactData && setEmergencyContact(emergencyContactData);
    }, []);

    const setEmergencyContact = (emergencyContactData: EmergencyContactData) => {
        if ('name' in emergencyContactData && emergencyContactData.name !== '') {
            const parts = emergencyContactData.name.split(' ');
            if (parts.length >= 2) {
                const lastName = parts.pop() ?? '';
                const firstName = parts.join(' ');
                setFirstName(firstName);
                setLastName(lastName);
            } else {
                setFirstName(emergencyContactData.name);
            }
        }
        if ('mail' in emergencyContactData && emergencyContactData.mail !== '') {
            setMail(emergencyContactData.mail);
        }
        if ('phone' in emergencyContactData && emergencyContactData.phone !== '') {
            const phone = emergencyContactData.phone;
            setPhoneData({
                phoneCountryPrefix: phone.slice(0, 3),
                phoneAreaCode: phone.slice(3, 6),
                phoneNumber: phone.slice(6, phone.length),
                lengthValid: true,
            });
        }
    };

    const handleCheckboxEvent = (e: KeyboardEvent) => {
        if (e.code === 'Enter' || e.code === 'Space') {
            e.preventDefault();
            setAgbChecked(!agbChecked);
        }
    };

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        setAlreadyTested(true);

        if (!formValid) {
            return;
        }
        setPageState(PageState.Loading);

        axios({
            url: window.$serviceUrl + '/bookingData?token=' + window.$token,
            method: 'POST',
            data: {
                requestType: 'bookingData',
                clientId: 'customerarea',
                requestSource: 'AurumTours-Customerarea',
                bookingId: storage.get('bookingNumber'),
                action: 'sendEmergencyContact',
                hash: storage.get('access_token'),
                data: {
                    firstName: firstName.trim(),
                    lastName: lastName.trim(),
                    phone: phoneNumber?.trim(),
                    mail: mail.trim(),
                },
            },
        })
            .then((response) => {
                if (response.status === 200) {
                    if (!window.$affiliate) {
                        window.$gtmTracking.gtag('event', 'page_success', { page: 'emergencyContact' });
                    }
                    tracking('success', 'emergencyContact');
                    setPageState(PageState.Success);
                    return;
                }
                setPageState(PageState.Error);
            })
            .catch(() => {
                setPageState(PageState.Error);
            })
            .finally(() => {
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                });
            });
    };

    return (
        <div className="page-emergency-contact redesign">
            <Header contactLayerMail={CONTACT.DIGITAL_TOUR_GUIDE_MAIL} />
            {pageState === PageState.Success ? (
                <StatusPage type="success" subPage="emergencyContact" contactName={`${firstName} ${lastName}`} />
            ) : pageState === PageState.Error ? (
                <StatusPage type="error" />
            ) : (
                <div className="content-wrapper">
                    <h1 className="main-headline">Ihr Notfallkontakt</h1>
                    <div className="intro-row">
                        Wir möchten Ihnen auch in unvorhersehbaren Situationen den besten Service bieten. Daher haben
                        Sie hier die Möglichkeit einen Kontakt zu hinterlegen, welchen wir im Notfall verständigen
                        können.
                    </div>
                    <form
                        className={`${pageState === PageState.Loading ? 'loading' : ''} ${textInputsValid ? 'valid' : ''}`}
                        onSubmit={handleSubmit}
                        onChange={() => tracking('interaction', 'emergencyContact')}>
                        <section className="card">
                            <div className="form-row flex">
                                <div className="width-expand width-1-2">
                                    <div
                                        className={`input-container${alreadyTested && !firstNameValid ? ' invalid' : ''}`}>
                                        <label htmlFor="input-first-name">Vorname</label>
                                        <input
                                            type="text"
                                            name="input-first-name"
                                            id="input-first-name"
                                            value={firstName}
                                            placeholder="Vorname"
                                            onChange={(e) => setFirstName(e.target.value)}
                                        />
                                        {firstName.length > 0 && (
                                            <i
                                                className="material-symbols-outlined reset-trigger"
                                                onClick={() => setFirstName('')}>
                                                clear
                                            </i>
                                        )}
                                    </div>
                                    {alreadyTested && !firstNameValid && (
                                        <div className="text-red error">Bitte geben Sie den Vornamen ein.</div>
                                    )}
                                </div>
                                <div className="width-expand width-1-2">
                                    <div
                                        className={`input-container ${alreadyTested && !lastNameValid ? ' invalid' : ''}`}>
                                        <label htmlFor="input-last-name">Nachname</label>
                                        <input
                                            type="text"
                                            name="input-last-name"
                                            id="input-last-name"
                                            value={lastName}
                                            placeholder="Nachname"
                                            onChange={(e) => setLastName(e.target.value)}
                                        />
                                        {lastName.length > 0 && (
                                            <i
                                                className="material-symbols-outlined reset-trigger"
                                                onClick={() => setLastName('')}>
                                                clear
                                            </i>
                                        )}
                                    </div>
                                    {alreadyTested && !lastNameValid && (
                                        <div className="text-red error">Bitte geben Sie den Nachnamen ein.</div>
                                    )}
                                </div>
                            </div>
                            <div className="form-row">
                                <div className={alreadyTested && !phoneValid ? ' invalid' : ''}>
                                    <PhoneInput
                                        label="Handynummer"
                                        phoneData={phoneData}
                                        showDACHOnly={true}
                                        setMobileNumber={(data: PhoneInputFieldData) => setPhoneData(data)}
                                    />
                                    {alreadyTested && !phoneValid && (
                                        <div className="text-red error">Bitte geben Sie die Handynummer ein.</div>
                                    )}
                                </div>
                            </div>
                            <div className="form-row">
                                <div className={`input-container ${alreadyTested && !mailValid ? 'invalid' : ''}`}>
                                    <label htmlFor="input-first-name">E-Mail Adresse</label>
                                    <input
                                        type="email"
                                        name="input-mail"
                                        id="input-mail"
                                        value={mail}
                                        placeholder="max.mustermann@beispielmail.de"
                                        onChange={(e) => setMail(e.target.value)}
                                    />
                                    {mail.length > 0 && (
                                        <i
                                            className="material-symbols-outlined reset-trigger"
                                            onClick={() => setMail('')}>
                                            clear
                                        </i>
                                    )}
                                </div>
                                {alreadyTested && !mailValid && (
                                    <div className="text-red error">Bitte geben Sie die E-Mail-Adresse ein.</div>
                                )}
                            </div>
                        </section>
                        <section className="accept-sec">
                            <input
                                id="checkbox-accept"
                                name="checkbox-accept"
                                type="checkbox"
                                checked={agbChecked}
                                onChange={() => setAgbChecked(!agbChecked)}
                                onKeyDown={handleCheckboxEvent}
                            />
                            <div>
                                <label htmlFor="checkbox-accept">
                                    Durch das Absenden Ihrer Daten sind Sie damit einverstanden, dass wir Sie zu
                                    dringlichen Anliegen bezüglich Ihrer gebuchten Reise kontaktieren. Sie sind außerdem
                                    damit einverstanden, dass wir Ihre Daten nach Datenschutzrichtlinien speichern und
                                    verarbeiten. Hier finden Sie unsere{' '}
                                    <NavLink to="/datenschutz" target="_blank">
                                        Datenschutzhinweise
                                    </NavLink>{' '}
                                    und{' '}
                                    <a
                                        href={`${window.$website}/unternehmen/agb`}
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        AGB
                                    </a>
                                    .
                                </label>
                                {alreadyTested && !agbChecked && (
                                    <div className="text-red error">
                                        Um das Formular abschicken zu können, müssen Sie den Bedingungen zustimmen.
                                    </div>
                                )}
                            </div>
                        </section>
                        <section className="user-action">
                            <BackButton target="blank" />
                            <Button
                                label={
                                    pageState !== PageState.Loading
                                        ? 'Kontaktdaten abschicken'
                                        : 'Daten werden übermittelt ...'
                                }
                                type="submit"
                                valid={formValid}
                                validated={pageState === PageState.Loading}
                            />
                        </section>
                    </form>
                </div>
            )}
            <Footer />
        </div>
    );
};
export default EmergencyContact;
